import { useIframeAuthFlow } from '~/composables/useIframeAuthFlow';
import log from '~/utils/log';

type AuthRedirectOptions = {
    to: { fullPath: string };
    refreshAttemptStateKey: string;
};

const getRefreshAttemptStorageKey = (refreshAttemptStateKey: string, fullPath: string) =>
    `${refreshAttemptStateKey}:${fullPath}`;

const removeRefreshAttempt = (refreshAttemptStateKey: string, fullPath: string) => {
    if (import.meta.server) return;

    try {
        sessionStorage.removeItem(getRefreshAttemptStorageKey(refreshAttemptStateKey, fullPath));
    } catch {
        // ignore storage cleanup errors
    }
};

/**
 * Redirects unauthenticated users by trying token refresh first and falling back
 * to interactive login. The refresh attempt is limited to one attempt per route
 * and persisted in sessionStorage to survive full-page OAuth redirects.
 *
 * @param options Route and state-key options used to track refresh attempts.
 * @returns A navigation redirect to `/api/auth/refresh` or `/api/auth/login`.
 */
const redirectToLogin = async (options: AuthRedirectOptions) => {
    const { shouldUseIframeAuthFlow } = useIframeAuthFlow();
    const nuxtApp = useNuxtApp();
    const query = new URLSearchParams();
    query.set('redirect_to', options.to.fullPath);

    const { runAll } = usePreLoginHooks();
    await runAll();

    if (shouldUseIframeAuthFlow.value) {
        const { useAuthPopup } = await import('~/composables/useAuthPopup');
        const { loginWithPopup } = useAuthPopup();
        log.debug('[useRedirectToLogin.ts] Opening login popup for iframe auth flow.');
        await loginWithPopup(options.to.fullPath);
        return;
    }

    log.debug('[useRedirectToLogin.ts] Redirecting to login');
    return nuxtApp.runWithContext(() => navigateTo(`/api/auth/login?${query.toString()}`, { external: true }));
};

export function useRedirectToLogin() {
    return { redirectToLogin, removeRefreshAttempt };
}
